import { c as _c } from "react/compiler-runtime";
import './gift-options-dialog.scss';
import { useEffect, useState } from 'react';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { ButtonStyleProps } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { Checkbox } from '@sola/ark-core/molecules/checkbox/checkbox.tsx';
import { Dialog, DialogActionClose, DialogActions, DialogContent, DialogTrigger } from '@sola/ark-core/molecules/dialog/dialog.tsx';
import { Form, FormSubmit } from '@sola/ark-core/molecules/form/form.tsx';
import { NumberField } from '@sola/ark-core/molecules/number-field/number-field.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { CommitmentFrequencyEnum } from '@sola/schema/dotcom/cart/commitments.ts';
import type { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildGiftLimitItem } from '@sola/schema/dotcom/proxy-api/giftLimits.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { ButtonAction } from '../../types/gifts.ts';
import { getCustomAmountLimits, getGiftAmountDefault, giftAmountOptionsBase, givingFrequencyOptions, isAmountOverRemaining, useGiftData } from './utils.ts';

// TODO: This may need to move to ./utils depending on where it needs to be used.
enum FormFieldNames {
  frequency = 'frequency',
  amount = 'amount',
  customAmount = 'custom-amount',
  addGiftToAll = 'add-to-all-children',
}
const ValidateCustomAmountOnMount: React.FC = () => {
  const $ = _c(3);
  const {
    trigger
  } = useFormContext();
  let t0;
  let t1;
  if ($[0] !== trigger) {
    t0 = () => {
      trigger(FormFieldNames.customAmount).catch(_temp);
    };
    t1 = [trigger];
    $[0] = trigger;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  return null;
};
export interface GiftOptionsSubmitData {
  frequency: CommitmentFrequencyEnum;
  // TODO: Some of these types may need to change depending on what the cart expects.
  amount?: number;
  addToAllEligible: boolean;
}
export interface GiftOptionsDialogProps {
  commitment: GraphQLCommitment;
  giftType: GiftTypeEnum;
  giftTypeLimits?: ChildGiftLimitItem;
  initialAmount?: number | null;
  initialFrequency?: CommitmentFrequencyEnum;
  showAddGiftToAllEligible?: boolean;
  /**
   * The card-level action that opened this dialog. Drives CTA copy
   * (Add/Edit/Set Up Recurring) and frequency options.
   */
  buttonAction?: ButtonAction;
  open?: boolean;
  openTrigger?: React.ReactElement<ButtonStyleProps>;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (data: GiftOptionsSubmitData) => Promise<void>;
}

// TODO: Will need to handle loading current state from cart
export const GiftOptionsDialog: React.FC<GiftOptionsDialogProps> = ({
  commitment,
  giftType,
  giftTypeLimits: giftTypeLimitsProp,
  initialAmount,
  initialFrequency,
  showAddGiftToAllEligible = false,
  buttonAction,
  open,
  openTrigger,
  onOpenChange,
  onSubmit
}) => {
  const {
    t
  } = useTranslation(['my', 'core', 'forms']);
  const [isDialogOpenInternal, setIsDialogOpenInternal] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [showSubmitError, setShowSubmitError] = useState(false);
  const isDialogOpen = open ?? isDialogOpenInternal;
  const {
    icon,
    title,
    supportingText,
    addButtonText,
    giftAmounts,
    giftAmountDefault,
    acceptCustomValue,
    frequencies
  } = useGiftData(t, giftType, commitment, buttonAction);
  const defaultAmount = getGiftAmountDefault(giftAmountDefault, giftAmounts, giftTypeLimitsProp?.remainingAmount);
  const selectedInitialAmount = initialAmount ?? defaultAmount;
  const isInitialAmountCustom = !!selectedInitialAmount && acceptCustomValue && !giftAmounts.includes(selectedInitialAmount);
  const initialAmountValue = isInitialAmountCustom ? giftAmountOptionsBase.other.value : selectedInitialAmount.toString();

  // Resolved default selection. Priority:
  //   1. SetupRecurring action -> Annually
  //   2. Cart's existing value -> that value (Edit case)
  //   3. Otherwise             -> OneTime
  const defaultFrequency = buttonAction === ButtonAction.SetupRecurring ? CommitmentFrequencyEnum.Annually : initialFrequency ?? CommitmentFrequencyEnum.OneTime;
  const [frequency, setFrequency] = useState(defaultFrequency);
  const [isOtherAmount, setIsOtherAmount] = useState(isInitialAmountCustom);
  const onFrequencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      value
    } = e.target;
    setFrequency(value as CommitmentFrequencyEnum);
  };
  const handleOpenChange = (nextOpen: boolean) => {
    setFrequency(defaultFrequency);
    setIsDialogOpenInternal(nextOpen);
    onOpenChange?.(nextOpen);
    setIsOtherAmount(nextOpen ? isInitialAmountCustom : false);
    setShowSubmitError(false);
  };
  const handleSubmit = async (data: FieldValues) => {
    if (!onSubmit) {
      return;
    }
    setIsBusy(true);
    setShowSubmitError(false);
    try {
      const rawAmount = Number(isOtherAmount ? data[FormFieldNames.customAmount] : data[FormFieldNames.amount]);
      const amount = Number.isNaN(rawAmount) ? 0 : rawAmount;
      await onSubmit({
        frequency,
        amount,
        addToAllEligible: Boolean(data[FormFieldNames.addGiftToAll])
      });
      handleOpenChange(false);
    } catch (error) {
      // Do not close the dialog, display the error to the user
      console.error('GiftOptionsDialog submit error:', error);
      setShowSubmitError(true);
    } finally {
      setIsBusy(false);
    }
  };
  const customAmountLimits = getCustomAmountLimits(giftTypeLimitsProp, frequency);

  // Show the "Other" custom amount option only when a valid min/max range exists.
  const showCustomAmountOption = acceptCustomValue && (!giftTypeLimitsProp || !!customAmountLimits);
  const isRemainingBelowMinimum = !!customAmountLimits && customAmountLimits.maxAmount < customAmountLimits.minAmount;
  const amountMinLimitMsg = customAmountLimits ? t('forms:errors.amountLimitsMin', {
    min: customAmountLimits.minAmount
  }) : '';
  const amountRemainingBelowMinMsg = customAmountLimits ? t('forms:errors.amountRemainingBelowMin', {
    min: customAmountLimits.minAmount,
    remaining: customAmountLimits.maxAmount
  }) : '';
  const amountMaxLimitMsg = customAmountLimits ? t('forms:errors.amountLimitsMax', {
    max: customAmountLimits.maxAmount
  }) : '';
  return <Dialog open={isDialogOpen} onOpenChange={handleOpenChange} isBusy={isBusy}>
      {openTrigger && <DialogTrigger>{openTrigger}</DialogTrigger>}
      <DialogContent size="sm" icon={icon} purpose="neutral" title={title} supportingText={supportingText} className="GiftOptionsDialog">
        {showSubmitError && <Alert className="mb-comfy-lg" variant="negative" title={t('my:gifts.dialog.addGiftError.title')} message={t('my:gifts.dialog.addGiftError.message')} action={<Link href={t('my:ctas.contactSupport.url')}>
                {t('my:ctas.contactSupport.text')}
              </Link>} />}

        <Form key={`${giftType}-${defaultFrequency}-${initialAmountValue}`} onSubmit={handleSubmit} options={{
        mode: 'onTouched',
        defaultValues: {
          [FormFieldNames.frequency]: defaultFrequency,
          [FormFieldNames.amount]: initialAmountValue,
          [FormFieldNames.customAmount]: isInitialAmountCustom ? selectedInitialAmount : undefined,
          [FormFieldNames.addGiftToAll]: false
        }
      }}>
          <div className="flex flex-col items-center gap-comfy-sm">
            {frequencies.length > 1 ? <RadioGroup name={FormFieldNames.frequency} variant="default" orientation="horizontal" items={givingFrequencyOptions.map(({
            value: value_0,
            translationKey
          }) => {
            return {
              value: value_0,
              text: t(translationKey),
              disabled: buttonAction === ButtonAction.SetupRecurring
            };
          })} value={frequency} onChange={onFrequencyChange} /> : null}

            <Select name={FormFieldNames.amount} label="Amount" selectClasses="GiftOptionsDialog__field" required items={[giftAmountOptionsBase.none, ...giftAmounts.map(val => ({
            value: val.toString(),
            text: t('core:numbers.currencyCustom', {
              amount: val
            }),
            disabled: isAmountOverRemaining(giftTypeLimitsProp?.remainingAmount, val)
          })), ...(showCustomAmountOption ? [giftAmountOptionsBase.other] : [])]} onChange={e_0 => setIsOtherAmount(e_0.target.value === giftAmountOptionsBase.other.value)} />

            {isOtherAmount && <>
                <NumberField label="Custom Amount" name={FormFieldNames.customAmount} inputClasses="GiftOptionsDialog__field" allowEmoji={false} prefix="$" required validate={isRemainingBelowMinimum ? () => amountRemainingBelowMinMsg : undefined} min={!isRemainingBelowMinimum && customAmountLimits?.minAmount ? {
              value: customAmountLimits.minAmount,
              message: amountMinLimitMsg
            } : undefined} max={!isRemainingBelowMinimum && customAmountLimits?.maxAmount ? {
              value: customAmountLimits.maxAmount,
              message: amountMaxLimitMsg
            } : undefined} />
                {isRemainingBelowMinimum && <ValidateCustomAmountOnMount />}
              </>}

            {showAddGiftToAllEligible && <Checkbox name={FormFieldNames.addGiftToAll} label={t('my:gifts.dialog.addGiftToEligibleChildrenLabel')} defaultChecked={false} />}
          </div>

          <DialogActions>
            <DialogActionClose disabled={isBusy}>
              {t('core:actions.cancel')}
            </DialogActionClose>

            <FormSubmit className="w-full md:w-fit" size="md" fullWidth={false} isBusy={isBusy} disableUntilFilled>
              {addButtonText}
            </FormSubmit>
          </DialogActions>
        </Form>
      </DialogContent>
    </Dialog>;
};
function _temp() {
  return false;
}